import * as is from 'is';
import { client } from '@utils/graphql/setup';
import {
  QueryCreator,
  CriteriaCreator,
  SingleRecordMutationCreator,
  MutationCreator,
} from './base';
import { createAcceptedProperties } from '../helpers';
import { ZonesQuery } from './zones';
import { ContactsQuery } from './contacts';
import { dataRegex } from '../helpers';
import apiKeysData from '../mocks/apiKeys.json';
import { getLogger } from '@utils/general';
const logger = getLogger('src.utils.graphQL.creators.apiKeys');

const acceptedReturnProperties = createAcceptedProperties(
  'id',
  'user_key',
  'description',
  'can_read_write',
  'can_request_snap',
  'can_use_srs',
  'can_set_token_expiration',
  'can_view_private_metadata',
  'date_created',
  'zone.id',
  'zone.name',
  'contact.id',
  'contact.name',
  'contact.title',
  'contact.companyName',
  'contact.primaryPhone',
  'contact.emailAddress'
);

const acceptedCriteriaProperties = {
  id: { type: 'string' },
  userKey: { type: 'string' },
  description: { type: 'string', triplequoted: true },
  canReadWrite: { type: 'boolean' },
  canRequestSnap: { type: 'boolean' },
  canUseSrs: { type: 'boolean' },
  canSetTokenExpiration: { type: 'boolean' },
  canViewPrivateMetadata: { type: 'boolean' },
  contactId: { type: 'string' },
  contactName: { type: 'string' },
  contactFirstName: { type: 'string' },
  contactLastName: { type: 'string' },
  zoneId: { type: 'string' },
  zoneName: { type: 'string' },
  dateCreated: { type: 'string' },
  or: { type: 'or' },
  and: { type: 'and' },
  not: { type: 'not' },
};

class ApiKeysCriteria extends CriteriaCreator {
  constructor(criteria = {}) {
    super(criteria);
  }
  get acceptedCriteria() {
    return acceptedCriteriaProperties;
  }
}

export class ApiKeysQuery extends QueryCreator {
  #contactQuery = new ContactsQuery();
  #zoneQuery = new ZonesQuery();

  constructor(queryType = 'apikeys', id = null, options = {}) {
    super(queryType, ApiKeysCriteria);

    if (id) {
      this.setId(id);
    }

    if (options?.pagination) {
      const { count_per_page, start_on_page } = options.pagination;
      if (count_per_page) this.addPagination('countPerPage', count_per_page);
      if (start_on_page) this.addPagination('startOnPage', start_on_page);
    }
    this.addPaginationReturn('total_pages', 'total_records', 'count_per_page', 'current_page');

    if (options?.sort) {
      this.setSort(options.sort.field, options.sort.direction);
    }
  }

  get acceptedReturnProperties() {
    return acceptedReturnProperties;
  }

  addReturnProperties(...properties) {
    for (const property of properties) {
      if (property.startsWith('zone')) {
        const [, ...zoneProperty] = property.split('.');
        this.#zoneQuery.addReturnProperties(zoneProperty.join('.'));
        continue;
      }
      if (property.startsWith('contact')) {
        const [, ...contactProperty] = property.split('.');
        this.#contactQuery.addReturnProperties(contactProperty.join('.'));
        continue;
      }
      super.addReturnProperties(property);
    }
    return this;
  }

  returnSyntax() {
    const syntax = super.returnSyntax();
    if (syntax === '') {
      return syntax;
    }

    const [, data] = syntax.match(dataRegex);
    const zoneSyntax = this.#zoneQuery.returnSyntax();
    const contactSyntax = this.#contactQuery.returnSyntax();

    const dataSyntax = [data.trim()];

    if (!is.empty(zoneSyntax)) {
      dataSyntax.push(
        zoneSyntax.replace(/^data/, 'zone').replace(/\n$/, '').trim()
      );
    }

    if (!is.empty(contactSyntax)) {
      dataSyntax.push(
        contactSyntax.replace(/^data/, 'contact').replace(/\n$/, '').trim()
      );
    }

    return `data {\n${dataSyntax.join('\n')}\n}`;
  }
}

export function getApiKeys(id, criteria = {}, options = {}) {
  let query;
  if (id) {
    query = new ApiKeysQuery('apikey', id, options);
  } else {
    query = new ApiKeysQuery('apikeys', null, options);
    if (!is.empty(criteria)) {
      Object.keys(criteria).forEach((key) => {
        query.addCriteria(key, criteria[key]);
      });
    }
  }

  return {
    query,
    execute() {
      return /^dev(?:elop(?:ment)?)?$/i.test(import.meta.env.VITE_ENV)
        ? Promise.resolve().then(() => {
          const apiKeys = id
            ? apiKeysData.find((apiKey) => apiKey.id === id)
            : criteria
              ? apiKeysData.filter((apiKey) => {
                return Object.keys(criteria).every((key) => apiKey[key] === criteria[key]);
              })
              : apiKeysData;
          return { data: apiKeys };
        })
        : client.query(query.name, query.toString());
    },
  };
}

export class SingleApiKeyMutation extends SingleRecordMutationCreator {
  constructor(queryType = 'create_api_key', criteria = {}) {
    super(queryType, ApiKeysCriteria);
    this.setCriteria(criteria);
  }
  get acceptedReturnProperties() {
    return acceptedReturnProperties;
  }
}

export class SaveApiKeyMutation extends MutationCreator {
  constructor(queryType = 'create_api_key', criteria = {}) {
    super(queryType, ApiKeysCriteria);
    this.setCriteria(criteria);
  }
  get acceptedReturnProperties() {
    return acceptedReturnProperties;
  }
}

export function saveApiKey(mutationType, input) {
  let mutation;
  mutation = new SaveApiKeyMutation(mutationType, input);

  return {
    mutation,
    execute() {
      return client.mutate(mutation.name, mutation.toString());
    },
  };
}

export function updateApiKey(mutationType, id, input = {}) {
  let mutation;
  mutation = new SingleApiKeyMutation(mutationType, input);
  mutation.setId(id);
  // add default return properties to override the base default 'name'
  // as apikeys do not have that property
  mutation.addReturnProperties('id', 'user_key');

  return {
    mutation,
    execute() {
      return client.mutate(mutation.name, mutation.toString());
    },
  };
}

export const deleteApiKey = async (apiKey) => {
  const { execute } = updateApiKey('remove_api_key', apiKey.id);

  try {
    await execute();
  }
  catch (error) {
    logger.error(error, `Failed to delete apiKey ${apiKey.id}.`);
    throw new Error(error);
  }
};